import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { TG_INSET_PRESETS } from "./presets"
import {
  IDevToolsState,
  ITgInsets,
  ITgInsetsPresetId,
  ITmaOverride
} from "./types"

const STORAGE_KEY = "devTools"

const defaultState: IDevToolsState = {
  tgInsets: TG_INSET_PRESETS.off,
  tgInsetsPresetId: "off",
  emulatePhoneDevice: false,
  tmaOverride: "auto"
}

export const readState = (): IDevToolsState => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultState
    const parsed = JSON.parse(raw) as Partial<IDevToolsState>
    return {
      tgInsets: { ...TG_INSET_PRESETS.off, ...parsed.tgInsets },
      tgInsetsPresetId: parsed.tgInsetsPresetId ?? "off",
      emulatePhoneDevice: parsed.emulatePhoneDevice ?? false,
      tmaOverride: parsed.tmaOverride ?? "auto"
    }
  } catch {
    return defaultState
  }
}

const writeState = (state: IDevToolsState) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore quota / disabled storage
  }
}

export const initialState: IDevToolsState = readState()

const clamp = (value: number) =>
  Math.max(0, Math.floor(Number.isFinite(value) ? value : 0))

export const DevToolsSlice = createSlice({
  name: "devTools",
  initialState,
  reducers: {
    setTgInsetsPreset: (
      state,
      { payload }: PayloadAction<ITgInsetsPresetId>
    ) => {
      if (payload === "custom") return
      state.tgInsets = { ...TG_INSET_PRESETS[payload] }
      state.tgInsetsPresetId = payload
      writeState(state)
    },
    setTgInsetValue: (
      state,
      { payload }: PayloadAction<{ key: keyof ITgInsets; value: number }>
    ) => {
      state.tgInsets[payload.key] = clamp(payload.value)
      state.tgInsetsPresetId = "custom"
      writeState(state)
    },
    resetTgInsets: (state) => {
      state.tgInsets = { ...TG_INSET_PRESETS.off }
      state.tgInsetsPresetId = "off"
      writeState(state)
    },
    setEmulatePhoneDevice: (state, { payload }: PayloadAction<boolean>) => {
      state.emulatePhoneDevice = payload
      writeState(state)
    },
    setTmaOverride: (state, { payload }: PayloadAction<ITmaOverride>) => {
      state.tmaOverride = payload
      writeState(state)
    }
  }
})

export const {
  setTgInsetsPreset,
  setTgInsetValue,
  resetTgInsets,
  setEmulatePhoneDevice,
  setTmaOverride
} = DevToolsSlice.actions
